import { el, append } from "../ui/dom";
import { signal, effect } from "../ui/signals";
import {
  pageHeader,
  prose,
  card,
  callout,
  codeBlock,
  rangeControl,
  stat,
  statRow,
  button,
} from "../ui/components";
import { h2, caption, ticker } from "./shared";
import { SpaceSaving } from "../core/space-saving";
import { formatCount, formatNumber, formatPercent } from "../ui/format";
import type { PageRenderer } from "../ui/router";

const VOCAB = 500;
const ZIPF_S = 1.3;

export const renderTopK: PageRenderer = (outlet) => {
  append(
    outlet,
    pageHeader({
      eyebrow: "Heavy hitters · top-K",
      title: "Top-K (Space-Saving)",
      lede: "Find the most frequent items in a stream — the top URLs, the noisiest errors, the hottest keys — using only K counters. Not the frequency of an item you name (that's Count-Min), but which items are hot.",
    }),
  );

  append(
    outlet,
    prose(
      el(
        "p",
        {},
        "Space-Saving keeps a fixed set of ",
        el("em", {}, "K"),
        " counters. A monitored item just increments. An unmonitored item ",
        el("strong", {}, "evicts the current minimum"),
        ": it takes that slot with count = min + 1 and remembers the evicted count as its possible error. Heavy hitters climb and stay; one-off items churn through the weakest slot. Any item whose true frequency exceeds ",
        el("strong", {}, "N/K"),
        " is guaranteed to survive — so the real hotspots can't hide.",
      ),
    ),
  );

  playgroundCard(outlet);

  append(outlet, h2("Use it"));
  append(
    outlet,
    codeBlock(
      `import { SpaceSaving } from "./core";

const top = new SpaceSaving({ capacity: 100 });
for (const path of requestLog) top.add(path);

top.topK(10);   // the 10 hottest paths, each {key, count, error}
top.estimate("/api/login"); // upper-bound count if monitored, else 0`,
    ),
  );

  append(
    outlet,
    callout(
      "info",
      el(
        "div",
        {},
        el("strong", {}, "Top-K vs. Count-Min. "),
        "A ",
        el("a", { href: "#/count-min" }, "Count-Min Sketch"),
        " answers “how often did ",
        el("em", {}, "this"),
        " item occur?” for an item you already have in hand. Space-Saving answers the complementary question — “what are the hot items?” — without you having to name any candidates. They're often deployed together.",
      ),
    ),
  );
};

// ---------------------------------------------------------------------------

function makeZipf(): () => string {
  const weights: number[] = [];
  let total = 0;
  for (let r = 1; r <= VOCAB; r++) {
    const w = 1 / Math.pow(r, ZIPF_S);
    weights.push(w);
    total += w;
  }
  const cum: number[] = [];
  let acc = 0;
  for (const w of weights) {
    acc += w / total;
    cum.push(acc);
  }
  return () => {
    const x = Math.random();
    let lo = 0;
    let hi = cum.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cum[mid] < x) lo = mid + 1;
      else hi = mid;
    }
    return `item-${lo + 1}`;
  };
}

function playgroundCard(outlet: HTMLElement): void {
  const capSig = signal(24);
  const { tick, bump } = ticker();

  const sample = makeZipf();
  const truth = new Map<string, number>();
  let ss = new SpaceSaving({ capacity: capSig.value });

  // Rebuild on capacity change (clears the run — counters can't be resized).
  effect(() => {
    ss = new SpaceSaving({ capacity: capSig.value });
    truth.clear();
    bump();
  });

  function feed(n: number): void {
    for (let i = 0; i < n; i++) {
      const t = sample();
      ss.add(t);
      truth.set(t, (truth.get(t) ?? 0) + 1);
    }
    bump();
  }

  const listHost = el("div", {});

  effect(() => {
    tick.value;
    const top = ss.topK(15);
    const trueSorted = [...truth].sort((a, b) => b[1] - a[1]);
    const trueTopKeys = new Set(trueSorted.slice(0, top.length).map(([k]) => k));
    const maxCount = top.length ? top[0].count : 1;

    listHost.replaceChildren(
      el(
        "div",
        { class: "topk-list" },
        ...top.map((c, i) => {
          const t = truth.get(c.key) ?? 0;
          const correct = trueTopKeys.has(c.key);
          const estW = (c.count / maxCount) * 100;
          const trueW = (t / maxCount) * 100;
          return el(
            "div",
            { class: "topk-row" },
            el("span", { class: "topk-rank" }, `${i + 1}`),
            el("span", { class: "topk-key" }, c.key),
            el(
              "span",
              { class: "topk-bar-track" },
              el("span", { class: "topk-bar est", style: { width: `${estW}%` } }),
              el("span", { class: "topk-bar-true", style: { left: `${trueW}%` } }),
            ),
            el("span", { class: `topk-nums${correct ? "" : " miss"}` }, formatNumber(c.count)),
          );
        }),
      ),
    );
  });

  const recall = (): string => {
    const k = Math.min(10, ss.size());
    if (k === 0) return "—";
    const trueTop = new Set([...truth].sort((a, b) => b[1] - a[1]).slice(0, k).map(([x]) => x));
    const got = ss.topK(k).filter((c) => trueTop.has(c.key)).length;
    return formatPercent(got / k);
  };

  const controls = el(
    "div",
    { class: "lab-controls" },
    el(
      "div",
      { class: "button-row" },
      button({ label: "+10K events", variant: "primary", onClick: () => feed(10000) }),
      button({ label: "+50K events", variant: "ghost", onClick: () => feed(50000) }),
      button({
        label: "Reset",
        variant: "ghost",
        onClick: () => {
          ss = new SpaceSaving({ capacity: capSig.value });
          truth.clear();
          bump();
        },
      }),
    ),
    rangeControl({
      label: "counters K",
      min: 4,
      max: 128,
      signal: capSig,
      format: (v) => String(v),
      hint: "more counters → more of the tail tracked",
    }),
    statRow(
      stat({ label: "Events (N)", value: () => (tick.value, formatCount(ss.total)) }),
      stat({ label: "Monitored", value: () => (tick.value, `${ss.size()} / ${capSig.value}`) }),
    ),
    statRow(
      stat({ label: "Top-10 recall", accent: true, value: () => (tick.value, recall()) }),
      stat({
        label: "Guarantee ≥",
        value: () => (tick.value, ss.total ? formatCount(ss.guaranteedThreshold()) : "—"),
        hint: "items above N/K are always kept",
      }),
    ),
    el(
      "p",
      { class: "mini-note" },
      "Green bars are the sketch's estimated counts; the ticked line marks the true count. A red number means that item isn't actually in the true top-K.",
    ),
  );

  const viz = el(
    "div",
    {},
    listHost,
    caption("Feed a skewed stream and the hottest items rise to the top, their estimates hugging the true counts."),
  );

  append(
    outlet,
    card({
      title: "Find the hottest items",
      subtitle: "A few terms dominate real traffic. Watch Space-Saving surface them from a stream it never fully stores.",
      children: [el("div", { class: "lab-layout" }, controls, viz)],
    }),
  );
}
